import type { CSSProperties } from 'react';
import { Suspense } from 'react';
import { appConfig } from 'shared';
import { useBodyClass } from '~/hooks/use-body-class';
import { useBreakpointAbove } from '~/hooks/use-breakpoints';
import { useMountedState } from '~/hooks/use-mounted-state';
import { useSheeter } from '~/modules/common/sheeter/use-sheeter';
import { FocusBridge, FocusTarget } from '~/modules/navigation/focus-bridge';
import { NavButton } from '~/modules/navigation/nav-buttons';
import { useNavigationStore } from '~/modules/navigation/navigation-store';
import { StopImpersonation } from '~/modules/navigation/stop-impersonation';
import type { NavItem, TriggerNavItemFn } from '~/modules/navigation/types';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
} from '~/modules/ui/sidebar';
import { navItems } from '~/nav-config';
import { lazyNamed } from '~/utils/lazy-named';

const DebugDropdown = __DEV_TOOLS__
  ? lazyNamed(() => import('~/modules/common/debug-dropdown'), 'DebugDropdown')
  : () => null;

const { hasSidebarTextLabels, sidebarWidthExpanded, sidebarWidthCollapsed, sheetPanelWidth } =
  appConfig.theme.navigation;

let baseNavItems: NavItem[] | null = null;
function getBaseNavItems() {
  if (!baseNavItems) baseNavItems = navItems.filter(({ type }) => type === 'base');
  return baseNavItems;
}

let footerNavItems: NavItem[] | null = null;
function getFooterNavItems() {
  if (!footerNavItems) footerNavItems = navItems.filter(({ type }) => (type as string) === 'footer');
  return footerNavItems;
}

interface SidebarNavProps {
  triggerNavItem: TriggerNavItemFn;
}

/** Widths are set as CSS custom properties; data attributes drive opacity, position and pointer-events. */
export function SidebarNav({ triggerNavItem }: SidebarNavProps) {
  const { hasStarted } = useMountedState();
  const isDesktop = useBreakpointAbove('2xl');

  const navSheetOpen = useNavigationStore((state) => state.navSheetOpen);
  const keepNavOpen = useNavigationStore((state) => state.keepNavOpen);

  const navSheetExists = useSheeter((state) => state.sheets.some((s) => s.id === 'nav-sheet' && s.open));

  useBodyClass({ 'keep-nav-open': keepNavOpen });
  useBodyClass({ 'nav-sheet-open': navSheetExists });

  const isCollapsed = !!navSheetOpen;
  const isExpanded = hasSidebarTextLabels && isDesktop && !isCollapsed;
  const isOverlay = !isDesktop || !keepNavOpen;

  // The spacer includes the sheet width when the sheet is open and not overlaying.
  const iconBarWidth = isExpanded ? sidebarWidthExpanded : sidebarWidthCollapsed;
  const sidebarWidth = iconBarWidth;
  const spacerWidth = isOverlay
    ? hasSidebarTextLabels && isDesktop
      ? sidebarWidthExpanded
      : sidebarWidthCollapsed
    : navSheetExists
      ? `calc(${iconBarWidth} + ${sheetPanelWidth})`
      : iconBarWidth;

  const cssVars = {
    '--icon-bar-w': iconBarWidth,
    '--sidebar-w': sidebarWidth,
    '--spacer-w': spacerWidth,
  } as CSSProperties;

  return (
    <div className="contents" style={cssVars}>
      {/* Pushes content; data-started keeps the width transition off on initial mount. */}
      <div
        data-slot="sidebar-spacer"
        data-started={hasStarted}
        className="relative w-(--spacer-w) bg-transparent group-[.focus-view]/body:hidden data-[started=true]:transition-[width] data-[started=true]:duration-300 data-[started=true]:ease-out"
      />
      <Sidebar
        id="sidebar-nav"
        collapsible="none"
        data-started={hasStarted}
        data-collapsed={isCollapsed}
        data-overlay={isOverlay}
        className="linear fixed inset-y-0 left-0 z-100 w-(--sidebar-w) border-r-0 transition-[width] duration-200 focus:outline-none group-[.focus-view]/body:hidden data-[started=false]:-translate-x-full"
      >
        <FocusTarget target="sidebar" />
        <div className="relative flex h-full flex-row">
          <div className="linear flex h-full w-(--icon-bar-w) flex-col overflow-hidden transition-[width] duration-200">
            <SidebarContent className="gap-1">
              <SidebarGroup className="p-0">
                <SidebarGroupContent>
                  <SidebarMenu className="gap-1">
                    {getBaseNavItems().map((navItem: NavItem) => (
                      <NavButton
                        key={navItem.id}
                        navItem={navItem}
                        isActive={navSheetOpen === navItem.id}
                        isCollapsed={!isExpanded}
                        onClick={triggerNavItem}
                      />
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            </SidebarContent>
            <SidebarFooter className="gap-2 p-0">
              <Suspense>{DebugDropdown ? <DebugDropdown className="mx-2" /> : null}</Suspense>
              <SidebarMenu className="gap-1">
                <StopImpersonation isCollapsed={!isExpanded} />
                {getFooterNavItems().map((navItem: NavItem) => (
                  <NavButton
                    key={navItem.id}
                    navItem={navItem}
                    isActive={navSheetOpen === navItem.id}
                    isCollapsed={!isExpanded}
                    onClick={triggerNavItem}
                  />
                ))}
              </SidebarMenu>
              <FocusBridge direction="to-sheet" className="focus:relative focus:m-2" />
            </SidebarFooter>
          </div>
        </div>
      </Sidebar>
    </div>
  );
}
